import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  LinearProgress
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RecyclingIcon from '@mui/icons-material/Recycling';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import SignalWifiIcon from '@mui/icons-material/SignalWifi4Bar';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import * as d3 from 'd3';
import { getBinByMacAddress, generateBinMockData } from '../../data/binMockData';

const TrashRecycleEntityDrawer = ({ entityDetails, chartRef }) => {
  const [selectedBin, setSelectedBin] = useState(null);
  const [allBinsData, setAllBinsData] = useState(null);

  useEffect(() => {
    // Generate all bins data
    const binsData = generateBinMockData();
    setAllBinsData(binsData);

    let foundBin = null;

    // Find the specific bin based on entityDetails name
    if (entityDetails && entityDetails.name) {
      foundBin = binsData.bins.find(bin => bin.name === entityDetails.name);
      
      // If not found by exact name, try MAC address as fallback
      if (!foundBin && entityDetails.keys) {
        const macAddressKey = entityDetails.keys.find(key => key.keyName === 'mac_address');
        if (macAddressKey) {
          foundBin = getBinByMacAddress(macAddressKey.value);
        }
      }
    }
    
    // Fallback to first bin if no specific bin found
    if (!foundBin && binsData.bins.length > 0) {
      foundBin = binsData.bins[0];
    }

    setSelectedBin(foundBin);
  }, [entityDetails]);

  const renderFillLevelChart = useCallback(() => {
    if (!selectedBin || !chartRef.current) return;
    d3.select(chartRef.current).selectAll('*').remove();

    const width = 400;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 60, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const data = selectedBin.timeSeries7d;

    const xScale = d3.scaleBand()
      .domain(data.map(d => d.time))
      .range([0, chartWidth])
      .padding(0.1);

    const yScale = d3.scaleLinear()
      .domain([0, 100])
      .range([chartHeight, 0]);

    const line = d3.line()
      .x(d => xScale(d.time) + xScale.bandwidth() / 2)
      .y(d => yScale(d.fillPercent));

    // Axes
    svg.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '10px');

    svg.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .text('Fill Level (%)')
      .style('font-size', '12px');

    // Line
    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#4CAF50')
      .attr('stroke-width', 3)
      .attr('d', line);

    // Dots + tooltips
    svg.selectAll('.fill-dot')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'fill-dot')
      .attr('cx', d => xScale(d.time) + xScale.bandwidth() / 2)
      .attr('cy', d => yScale(d.fillPercent))
      .attr('r', 4)
      .attr('fill', '#4CAF50')
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).attr('r', 6);
        const tooltip = d3.select(chartRef.current)
          .append('div')
          .attr('class', 'tooltip')
          .style('opacity', 0)
          .style('position', 'absolute')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none');
        tooltip.transition().duration(200).style('opacity', 0.9);
        tooltip.html(`
          <strong>${d.time}</strong><br/>
          Fill: ${d.fillPercent}%
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this).attr('r', 4);
        d3.select(chartRef.current).selectAll('.tooltip').remove();
      });

    // Title
    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Fill Level - Last 7 Days');
  }, [selectedBin]);

  useEffect(() => {
    if (chartRef.current && selectedBin && selectedBin.timeSeries7d.length > 0) {
      renderFillLevelChart();
    }
  }, [selectedBin, renderFillLevelChart]);

  // Show loading state if no bin data
  if (!selectedBin || !allBinsData) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading bin data...
        </Typography>
      </Box>
    );
  }

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Critical': return 'error';
      case 'Needs Attention': return 'warning';
      case 'Overdue': return 'error';
      default: return 'success';
    }
  };

  return (
    <>
      {/* Bin Header */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center">
              {selectedBin.type === 'Recycle' ? (
                <RecyclingIcon sx={{ mr: 1, color: 'green' }} />
              ) : (
                <DeleteOutlineIcon sx={{ mr: 1, color: 'primary.main' }} />
              )}
              <Box>
                <Typography variant="h6">
                  {selectedBin.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedBin.id} • {selectedBin.location}
                </Typography>
              </Box>
            </Box>
            <Chip 
              label={selectedBin.status} 
              color={getStatusColor(selectedBin.status)}
              variant="outlined"
            />
          </Box>

          {/* Status Alert */}
          {selectedBin.status !== 'OK' && (
            <Alert severity={getStatusColor(selectedBin.status)} sx={{ mb: 2 }}>
              {selectedBin.status === 'Critical' && 'Bin is critically full and needs immediate attention!'}
              {selectedBin.status === 'Needs Attention' && 'Bin is getting full and should be emptied soon.'}
              {selectedBin.status === 'Overdue' && 'Bin cleaning is overdue. Please schedule maintenance.'}
            </Alert>
          )}

          {/* Key Metrics */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Key Metrics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography 
                    variant="h6" 
                    color={selectedBin.fillPercent > 80 ? 'error.main' : 
                           selectedBin.fillPercent > 60 ? 'warning.main' : 'success.main'}
                  >
                    {selectedBin.fillPercent}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Current Fill
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="success.main">
                    {new Date(selectedBin.lastClearedAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Last Cleared
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="info.main">
                    {new Date(selectedBin.nextCleaningAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Next Cleaning
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Sensor Status */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Sensor Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Box display="flex" alignItems="center" flexDirection="column">
                <BatteryFullIcon color={selectedBin.sensorData.batteryLevel > 20 ? 'success' : 'error'} />
                <Typography variant="caption">{selectedBin.sensorData.batteryLevel}%</Typography>
                <Typography variant="caption" color="textSecondary">Battery</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box display="flex" alignItems="center" flexDirection="column">
                <SignalWifiIcon color={selectedBin.sensorData.signalStrength > -60 ? 'success' : 'warning'} />
                <Typography variant="caption">{selectedBin.sensorData.signalStrength} dBm</Typography>
                <Typography variant="caption" color="textSecondary">Signal</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box display="flex" alignItems="center" flexDirection="column">
                <ThermostatIcon color="primary" />
                <Typography variant="caption">{selectedBin.sensorData.temperature}°C</Typography>
                <Typography variant="caption" color="textSecondary">Temp</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box display="flex" alignItems="center" flexDirection="column">
                <Typography variant="caption">{selectedBin.sensorData.humidity}%</Typography>
                <Typography variant="caption" color="textSecondary">Humidity</Typography>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Last ping: {new Date(selectedBin.sensorData.lastPing).toLocaleTimeString()}
          </Typography>
        </CardContent>
      </Card>

      {/* Fill Level Chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <RecyclingIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              {`Fill Level Trend (7 Days) - ${selectedBin.id}`}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Percentage filled as measured by infra distance sensors. Fill levels vary based on location crowd patterns.
          </Typography>
          <Box 
            ref={chartRef}
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '320px'
            }}
          />
        </CardContent>
      </Card>

      {/* Location & System Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Location Information
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Location Type" 
                secondary={selectedBin.locationType?.replace(/_/g, ' ') || 'Unknown'} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Coordinates" 
                secondary={`${selectedBin.coordinates.latitude.toFixed(6)}, ${selectedBin.coordinates.longitude.toFixed(6)}`} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="MAC Address" 
                secondary={selectedBin.macAddress} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            System Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {allBinsData.summary.totalBins}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total Bins
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Typography variant="h6" color="warning.main">
                  {allBinsData.summary.needsAttention}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Need Attention
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default TrashRecycleEntityDrawer;


